import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { API_CONFIG } from '../config/api';

const inputStyle = {
  width: '100%', padding: '11px 14px',
  background: '#f8fafc',
  border: '1px solid rgba(15,23,42,0.14)',
  borderRadius: 10, color: '#0f172a', fontSize: '0.9rem',
  outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s',
};
const labelStyle = {
  display: 'block', color: 'rgba(15,23,42,0.65)',
  fontSize: '0.8rem', fontWeight: 500, marginBottom: 6,
};
const hintStyle = { color: 'rgba(15,23,42,0.4)', fontSize: '0.72rem', marginTop: 4 };
const onFocus = e => e.target.style.borderColor = 'rgba(124,58,237,0.6)';
const onBlur  = e => e.target.style.borderColor = 'rgba(15,23,42,0.14)';

const card = {
  background: 'rgba(255,255,255,0.8)',
  backdropFilter: 'blur(24px)',
  WebkitBackdropFilter: 'blur(24px)',
  border: '1px solid rgba(15,23,42,0.08)',
  borderRadius: 20,
  padding: '32px 28px',
  boxShadow: '0 32px 80px rgba(15,23,42,0.12), 0 0 0 1px rgba(124,58,237,0.06)',
  width: '100%',
};

const PrimaryBtn = ({ disabled, loading, children, ...props }) => (
  <button
    disabled={disabled}
    style={{
      width: '100%', padding: '12px', borderRadius: 10, border: 'none',
      cursor: disabled ? 'not-allowed' : 'pointer',
      fontWeight: 600, fontSize: '0.92rem', color: '#fff',
      background: disabled ? 'rgba(139,92,246,0.4)' : 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)',
      boxShadow: disabled ? 'none' : '0 4px 20px rgba(124,58,237,0.3)',
      transition: 'all 0.2s',
    }}
    {...props}
  >
    {loading
      ? <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <span style={{ width: 15, height: 15, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />
          {children}
        </span>
      : children}
  </button>
);

const Header = ({ onSwitchToLogin, currentStep }) => (
  <>
    <button onClick={onSwitchToLogin} style={{ background: 'none', border: 'none', color: '#7c3aed', fontSize: '0.8rem', cursor: 'pointer', padding: 0, marginBottom: 18, display: 'flex', alignItems: 'center', gap: 4 }}>
      ← Back to Login
    </button>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 18 }}>
      <img src="/cognixaihub_new_logo.png" alt="CognixAI Hub" style={{ width: 102, height: 34, objectFit: 'contain' }} />
    </div>
    <h2 style={{ color: '#0f172a', fontSize: '1.25rem', fontWeight: 700, textAlign: 'center', marginBottom: 4 }}>Create Your Account</h2>
    <p style={{ color: 'rgba(15,23,42,0.5)', fontSize: '0.8rem', textAlign: 'center', marginBottom: 20 }}>Join CognixAI and start your data journey</p>
    {/* Step indicator */}
    <div style={{ display: 'flex', gap: 6, marginBottom: 22 }}>
      {['Your Info', 'Verify Email', 'Set Password'].map((label, i) => {
        const active = i + 1 === currentStep;
        const done   = i + 1 < currentStep;
        return (
          <div key={label} style={{ flex: 1, padding: '7px 0', borderRadius: 8, textAlign: 'center', fontSize: '0.72rem', fontWeight: 600,
            background: active ? 'linear-gradient(135deg, #7c3aed, #4f46e5)' : done ? 'rgba(124,58,237,0.12)' : 'rgba(15,23,42,0.04)',
            color: active ? '#fff' : done ? '#7c3aed' : 'rgba(15,23,42,0.35)',
            border: done ? '1px solid rgba(124,58,237,0.25)' : '1px solid transparent',
          }}>
            {done ? '✓ ' : ''}{label}
          </div>
        );
      })}
    </div>
  </>
);

// ── Inline OTP step (light-themed, no overlay) ────────────────────────────────
function OTPStep({ email, onVerified, onBack, onSwitchToLogin }) {
  const [otp, setOtp]               = useState(['', '', '', '', '', '']);
  const [loading, setLoading]       = useState(false);
  const [resending, setResending]   = useState(false);
  const [timer, setTimer]           = useState(0);
  const [error, setError]           = useState('');
  const [success, setSuccess]       = useState('');
  const inputRefs                   = useRef([]);

  useEffect(() => { inputRefs.current[0]?.focus(); }, []);
  useEffect(() => {
    if (timer > 0) { const t = setTimeout(() => setTimer(t => t - 1), 1000); return () => clearTimeout(t); }
  }, [timer]);

  const handleChange = (i, val) => {
    if (!/^[0-9]?$/.test(val)) return;
    const next = [...otp]; next[i] = val; setOtp(next);
    if (val && i < 5) inputRefs.current[i + 1]?.focus();
  };

  const handleKey = (i, e) => {
    if (e.key === 'Backspace' && !otp[i] && i > 0) inputRefs.current[i - 1]?.focus();
    if (e.key === 'Enter') handleVerify();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const digits = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (digits.length === 6) { setOtp(digits.split('')); inputRefs.current[5]?.focus(); }
  };

  const handleVerify = async () => {
    const code = otp.join('');
    if (code.length !== 6) { setError('Enter all 6 digits'); return; }
    setLoading(true); setError('');
    try {
      const res = await axios.post('auth/verify-otp', { email, otp: code });
      if (res.data.success) { setSuccess('Email verified!'); setTimeout(onVerified, 800); }
      else { setError(res.data.message || 'Invalid code'); setOtp(['','','','','','']); inputRefs.current[0]?.focus(); }
    } catch (err) {
      setError(err.response?.data?.detail || 'Verification failed'); setOtp(['','','','','','']); inputRefs.current[0]?.focus();
    } finally { setLoading(false); }
  };

  const handleResend = async () => {
    setResending(true); setError(''); setSuccess('');
    try {
      const res = await axios.post('auth/resend-otp', { email, username: '' });
      if (res.data.success) { setSuccess('Code resent!'); setTimer(60); setOtp(['','','','','','']); inputRefs.current[0]?.focus(); }
      else setError(res.data.message || 'Failed to resend');
    } catch (err) { setError(err.response?.data?.detail || 'Failed to resend'); }
    finally { setResending(false); }
  };

  const filled = otp.join('').length === 6;

  return (
    <div style={card}>
      <Header onSwitchToLogin={onSwitchToLogin} currentStep={2} />

      <p style={{ textAlign: 'center', color: 'rgba(15,23,42,0.55)', fontSize: '0.82rem', marginBottom: 6 }}>
        Enter the 6-digit code sent to
      </p>
      <p style={{ textAlign: 'center', color: '#7c3aed', fontSize: '0.84rem', fontWeight: 600, marginBottom: 22 }}>
        {email}
      </p>

      {/* OTP inputs */}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 20 }}>
        {otp.map((digit, i) => (
          <input key={i} ref={el => inputRefs.current[i] = el}
            id={`otp-${i}`} type="text" inputMode="numeric" maxLength={1}
            value={digit}
            onChange={e => handleChange(i, e.target.value)}
            onKeyDown={e => handleKey(i, e)}
            onPaste={handlePaste}
            disabled={loading}
            style={{
              width: 44, height: 52, textAlign: 'center', fontSize: '1.3rem', fontWeight: 700,
              background: digit ? 'rgba(124,58,237,0.10)' : '#f8fafc',
              border: `2px solid ${digit ? 'rgba(124,58,237,0.55)' : 'rgba(15,23,42,0.14)'}`,
              borderRadius: 10, color: '#0f172a', outline: 'none',
              transition: 'border-color 0.15s, background 0.15s',
            }}
            onFocus={e => e.target.style.borderColor = 'rgba(124,58,237,0.75)'}
            onBlur={e => e.target.style.borderColor = digit ? 'rgba(124,58,237,0.55)' : 'rgba(15,23,42,0.14)'}
          />
        ))}
      </div>

      {error   && <div style={{ background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.25)', borderRadius:8, padding:'10px 14px', color:'#dc2626', fontSize:'0.82rem', marginBottom:12, textAlign:'center' }}>{error}</div>}
      {success && <div style={{ background:'rgba(16,185,129,0.08)', border:'1px solid rgba(16,185,129,0.25)', borderRadius:8, padding:'10px 14px', color:'#059669', fontSize:'0.82rem', marginBottom:12, textAlign:'center' }}>{success}</div>}

      <PrimaryBtn onClick={handleVerify} disabled={!filled || loading} loading={loading}>
        {loading ? 'Verifying…' : 'Verify Email'}
      </PrimaryBtn>

      <div style={{ textAlign:'center', marginTop: 14 }}>
        <p style={{ color:'rgba(15,23,42,0.5)', fontSize:'0.78rem', marginBottom: 8 }}>Didn't receive the code?</p>
        <button onClick={handleResend} disabled={resending || timer > 0}
          style={{ background:'none', border:'none', color: timer > 0 ? 'rgba(15,23,42,0.35)' : '#7c3aed', fontSize:'0.82rem', cursor: timer > 0 ? 'default' : 'pointer', fontWeight:500 }}>
          {resending ? 'Sending…' : timer > 0 ? `Resend in ${timer}s` : 'Resend Code'}
        </button>
      </div>

      <button onClick={onBack} style={{ width:'100%', marginTop:14, padding:'10px', background:'rgba(15,23,42,0.03)', border:'1px solid rgba(15,23,42,0.1)', borderRadius:10, color:'rgba(15,23,42,0.55)', fontSize:'0.84rem', cursor:'pointer' }}>
        ← Change Email
      </button>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

export default function EnhancedRegistration({ onSwitchToLogin }) {
  const [step, setStep]         = useState('info');      // 'info' | 'otp' | 'credentials' | 'pending'
  const [pendingApproval, setPendingApproval] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '', last_name: '', email: '',
    username: '', password: '', confirmPassword: '',
  });
  const [error,   setError]   = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();

  const set = (field) => (e) => setFormData(f => ({ ...f, [field]: e.target.value }));

  // ── Step 1: info ────────────────────────────────────────────────────────
  const handleInfoSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Please enter a valid email address'); return;
    }
    setLoading(true);
    try {
      // send-otp checks email existence first, then sends OTP
      const res = await axios.post('auth/send-otp', { email: formData.email, username: '' });
      if (res.data.success) {
        if (res.data.otp) { console.log('DEBUG OTP:', res.data.otp); alert(`DEBUG MODE: Your OTP is: ${res.data.otp}`); }
        setStep('otp');
      } else {
        setError(res.data.message || 'Failed to send verification code');
      }
    } catch (err) {
      const status = err.response?.status;
      if (status === 409) setError('An account with this email already exists. Please sign in.');
      else setError(err.response?.data?.detail || 'Failed to send verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Step 3: credentials ─────────────────────────────────────────────────
  const handleCredentialsSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!formData.username.trim()) { setError('Please choose a username'); return; }
    if (formData.password.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (formData.password !== formData.confirmPassword) { setError('Passwords do not match'); return; }
    setLoading(true);
    try {
      const result = await register(formData);
      if (result.success) {
        setPendingApproval(result.pending_approval ?? false);
        setStep('pending');
      } else {
        setError(result.message || 'Registration failed. Please try again.');
      }
    } catch {
      setError('Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Pending ─────────────────────────────────────────────────────────────
  if (step === 'pending') {
    return (
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20 }}>
          <img src="/cognixaihub_new_logo.png" alt="CognixAI Hub" style={{ width: 102, height: 34, objectFit: 'contain' }} />
        </div>
        <div style={{ textAlign: 'center', padding: '12px 0 20px' }}>
          <div style={{ fontSize: 44, marginBottom: 14 }}>{pendingApproval ? '⏳' : '✅'}</div>
          <h3 style={{ color: '#0f172a', fontSize: '1.1rem', fontWeight: 700, marginBottom: 8 }}>
            {pendingApproval ? 'Account Under Review' : "You're all set!"}
          </h3>
          <p style={{ color: 'rgba(15,23,42,0.55)', fontSize: '0.84rem', lineHeight: 1.6, marginBottom: 22 }}>
            {pendingApproval
              ? 'Your account is pending admin approval. You will be notified once approved.'
              : 'Account created! Sign in with your username and password.'}
          </p>
          <PrimaryBtn onClick={onSwitchToLogin} style={{ width: 'auto', padding: '10px 28px' }}>Back to Login</PrimaryBtn>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // ── OTP step ─────────────────────────────────────────────────────────────
  if (step === 'otp') {
    return <OTPStep
      email={formData.email}
      onVerified={() => { setError(''); setStep('credentials'); }}
      onBack={() => setStep('info')}
      onSwitchToLogin={onSwitchToLogin}
    />;
  }

  // ── Step 3: username + password ──────────────────────────────────────────
  if (step === 'credentials') {
    return (
      <div style={card}>
        <Header onSwitchToLogin={onSwitchToLogin} currentStep={3} />
        <form onSubmit={handleCredentialsSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={labelStyle}>Username</label>
            <input type="text" value={formData.username} onChange={set('username')}
              placeholder="Choose a username" required disabled={loading}
              style={inputStyle} onFocus={onFocus} onBlur={onBlur} />
          </div>
          <div>
            <label style={labelStyle}>Password</label>
            <input type="password" value={formData.password} onChange={set('password')}
              placeholder="Min 6 characters" required minLength={6} disabled={loading}
              style={inputStyle} onFocus={onFocus} onBlur={onBlur} />
          </div>
          <div>
            <label style={labelStyle}>Confirm Password</label>
            <input type="password" value={formData.confirmPassword} onChange={set('confirmPassword')}
              placeholder="Confirm your password" required disabled={loading}
              style={inputStyle} onFocus={onFocus} onBlur={onBlur} />
          </div>

          {error && <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 8, padding: '10px 14px', color: '#dc2626', fontSize: '0.82rem' }}>{error}</div>}

          <PrimaryBtn type="submit" disabled={loading} loading={loading}>
            {loading ? 'Creating Account…' : 'Create Account'}
          </PrimaryBtn>
        </form>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // ── Step 1: name + email ──────────────────────────────────────────────────
  return (
    <div style={card}>
      <Header onSwitchToLogin={onSwitchToLogin} currentStep={1} />
      <form onSubmit={handleInfoSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div>
            <label style={labelStyle}>First Name</label>
            <input type="text" value={formData.first_name} onChange={set('first_name')}
              placeholder="First name" required disabled={loading}
              style={inputStyle} onFocus={onFocus} onBlur={onBlur} />
          </div>
          <div>
            <label style={labelStyle}>Last Name</label>
            <input type="text" value={formData.last_name} onChange={set('last_name')}
              placeholder="Last name" required disabled={loading}
              style={inputStyle} onFocus={onFocus} onBlur={onBlur} />
          </div>
        </div>

        <div>
          <label style={labelStyle}>Email Address</label>
          <input type="email" value={formData.email} onChange={set('email')}
            placeholder="Enter your email address" required disabled={loading}
            style={inputStyle} onFocus={onFocus} onBlur={onBlur} />
          <p style={hintStyle}>We'll send a verification code to this email</p>
        </div>

        {error && <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 8, padding: '10px 14px', color: '#dc2626', fontSize: '0.82rem' }}>{error}</div>}

        <PrimaryBtn type="submit" disabled={loading} loading={loading}>
          {loading ? 'Checking…' : 'Continue →'}
        </PrimaryBtn>

        <p style={{ textAlign: 'center', color: 'rgba(15,23,42,0.5)', fontSize: '0.8rem', marginTop: 2 }}>
          Already have an account?{' '}
          <button type="button" onClick={onSwitchToLogin}
            style={{ background: 'none', border: 'none', color: '#7c3aed', fontSize: '0.8rem', cursor: 'pointer', padding: 0, fontWeight: 500 }}>
            Sign In
          </button>
        </p>
      </form>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
